import React from "react";

import kanaExpressionInterpreter from "../../../../../../../../lib/KanaExpressionInterpreter";
import { GetProjectDetailsResponse as GetPublicProjectDetailsResponse } from "../../../../../../../../service/publicquery";
import { GetProjectDetailsResponse as GetPrivateProjectDetailsResponse } from "../../../../../../../../service/query";
import {
  constructContext,
  convertDateToStringDDMMYYYY,
  jsonReviver,
  roundToDecimalPlaces,
} from "../../../../../../../../utils";
import { ChartData, ChartDataWithSuffix, KanaTooltip, TooltipProps } from "../../../../../../../../widget";
import { convertExpressionResultToCorrectType, convertToCorrectType } from "../../../../../../../developer/activities";
import { Component, DataType, HasKey, StandardSpecificData, StandardSpecificStepData, Step } from "../types";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const stepHasEditableComponents = (step: Step, expressionContext: any): boolean => {
  if (step.steps) {
    return step.steps.some((childStep: Step) => stepHasEditableComponents(childStep, expressionContext));
  }
  if (step.components) {
    return step.components.some((component: Component) => {
      return (
        component.editableConditionExpression !== undefined &&
        kanaExpressionInterpreter(component.editableConditionExpression, expressionContext) &&
        !(
          component.conditionExpression !== undefined &&
          !kanaExpressionInterpreter(component.conditionExpression, expressionContext)
        )
      );
    });
  }
  return false;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const stepHasDisplayableComponents = (step: Step, expressionContext: any): boolean => {
  if (step.steps) {
    return step.steps.some((childStep: Step) => stepHasDisplayableComponents(childStep, expressionContext));
  }
  if (step.components) {
    return step.components.some((component: Component) => {
      return !(
        component.conditionExpression !== undefined &&
        !kanaExpressionInterpreter(component.conditionExpression, expressionContext)
      );
    });
  }
  return false;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const reduceComponents = (components: Component[], expressionContext: any): Component[][] => {
  return components
    .filter(
      (component: Component) =>
        !(
          component.conditionExpression !== undefined &&
          !kanaExpressionInterpreter(component.conditionExpression, expressionContext)
        )
    )
    .reduce((acc: Component[][], component: Component, componentIdx: number) => {
      if (componentIdx % 2 === 0) {
        acc.push([component]);
      } else {
        acc[acc.length - 1].push(component);
      }
      return acc;
    }, []);
};

export const reduceChartComponents = (
  components: Component[],
  data: StandardSpecificData,
  key: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  expressionContext: any
): ChartData => {
  return components.reduce((acc: ChartData, component: Component) => {
    const combinedKey = `${key}.${component.key}`;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const value: any = kanaExpressionInterpreter(`@standardSpecific.${combinedKey}` as string, expressionContext);

    if (value !== undefined && value !== null) {
      acc[`${component.componentProperties.label}`] = value as number;
    }
    return acc;
  }, {});
};

export const reduceChartComponentsWithSuffix = (
  components: Component[],
  data: StandardSpecificData,
  key: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  expressionContext: any
): ChartDataWithSuffix => {
  return components.reduce((acc: ChartDataWithSuffix, component: Component) => {
    const combinedKey = `${key}.${component.key}`;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const value: any = kanaExpressionInterpreter(`@standardSpecific.${combinedKey}` as string, expressionContext);

    if (value !== undefined && value !== null) {
      acc[`${component.componentProperties.label}`] = {
        value: value as number,
        suffix: component.componentProperties.suffix,
      };
    }
    return acc;
  }, {});
};

export const createExpressionContext = (
  standardSpecificData: StandardSpecificData,
  projectDetails: GetPrivateProjectDetailsResponse | GetPublicProjectDetailsResponse | undefined
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any => {
  // A No standard project (or one without standard-specific data yet) has no data to
  // interpret, so use an empty context rather than passing null into constructContext.
  const standardSpecific = standardSpecificData ? constructContext(standardSpecificData) : {};
  /*
    Schemas nest their fields under a top-level step keyed "standardSpecificData", so the full path is
    `@standardSpecific.standardSpecificData.general.x`. `@standardSpecificData.general.x` is accepted as a
    shorthand for the same values, since schema expressions are written against the data's own keys.
  */
  return { standardSpecific, standardSpecificData: standardSpecific.standardSpecificData, project: projectDetails };
};

/**
 * Returns a copy of the data with the value at `dataPath` (`stepKey[.childStepKey].componentKey`) set,
 * creating any step along the path that does not exist yet. Never mutates `data`.
 */
export const setStandardSpecificValue = (
  data: StandardSpecificData,
  dataPath: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any,
  dataType: DataType
): StandardSpecificData => {
  const keys = dataPath.split(".");
  // make a copy of the data to avoid mutating the state directly
  const newData = JSON.parse(JSON.stringify(data ?? {}), jsonReviver);

  let currentStep: StandardSpecificData | StandardSpecificStepData | undefined = newData;

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];

    if (currentStep !== undefined) {
      if (i === keys.length - 1) {
        // if it's the last key, update the value
        if (!currentStep.data) {
          currentStep.data = {}; // Initialize data if it doesn't exist
        }
        currentStep.data[key] = convertToCorrectType(value, dataType);
      } else {
        // navigate to the next step
        let nextStep = (currentStep.steps || []).find((step: HasKey) => step.key === key);

        // if the step doesn't exist, create it
        if (!nextStep) {
          nextStep = {
            key,
            steps: i < keys.length - 2 ? [] : undefined,
            data: i === keys.length - 2 ? {} : undefined,
          };
          currentStep.steps = currentStep.steps || [];
          currentStep.steps.push(nextStep);
        }

        currentStep = nextStep;
      }
    }
  }

  return newData;
};

/** How many decimal places a calculated Number default keeps when the schema does not say. */
export const DEFAULT_VALUE_DECIMAL_PLACES = 2;

/**
 * The value a component's `defaultValueExpression` gives, converted to its dataType. Undefined when
 * the component has no expression. Throws when the expression cannot be evaluated.
 *
 * Number defaults are rounded - to `componentProperties.decimalPlaces` when the schema sets it, else
 * DEFAULT_VALUE_DECIMAL_PLACES - so a calculation such as area / 30 does not store a long float.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const evaluateComponentDefaultValue = (component: Component, expressionContext: any): unknown => {
  if (component.defaultValueExpression === undefined) return undefined;

  const value = convertExpressionResultToCorrectType(
    kanaExpressionInterpreter(component.defaultValueExpression, expressionContext),
    component.dataType
  );

  if (component.dataType !== "Number" || typeof value !== "number" || !Number.isFinite(value)) return value;

  return roundToDecimalPlaces(value, component.componentProperties?.decimalPlaces ?? DEFAULT_VALUE_DECIMAL_PLACES);
};

/**
 * The stored value at `dataPath`, or the component's default when nothing is stored, so an empty field
 * displays its default before the section has ever been saved.
 *
 * A default that fails to evaluate displays as no value; the failure is reported, once, when the
 * section saves (see applyComponentDefaultValues) rather than on every render.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getComponentDisplayValue = (component: Component, dataPath: string, expressionContext: any): any => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const storedValue: any = kanaExpressionInterpreter(`@standardSpecific.${dataPath}`, expressionContext);
  if (storedValue !== undefined && storedValue !== null) return storedValue;

  try {
    return evaluateComponentDefaultValue(component, expressionContext) ?? storedValue;
  } catch {
    return storedValue;
  }
};

/**
 * Calls `callback` for every visible component that has a defaultValueExpression, with its data path.
 * Paths mirror the ones the section renders: `stepKey[.childStepKey].componentKey`.
 */
const forEachDefaultValueComponent = (
  steps: Step[] | undefined,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  expressionContext: any,
  callback: (component: Component, dataPath: string) => void
): void => {
  const visit = (step: Step, parentPath: string | undefined): void => {
    const stepPath = parentPath ? `${parentPath}.${step.key}` : step.key;

    step.steps?.forEach((childStep) => visit(childStep, stepPath));

    step.components?.forEach((component) => {
      if (component.defaultValueExpression === undefined || component.component === "Paragraph") return;
      if (
        component.conditionExpression !== undefined &&
        !kanaExpressionInterpreter(component.conditionExpression, expressionContext)
      )
        return;

      callback(component, `${stepPath}.${component.key}`);
    });
  };

  steps?.forEach((step) => visit(step, undefined));
};

const isEmptyValue = (value: unknown): boolean => value === undefined || value === null;

/**
 * Whether two field values are the same. Numbers allow for rounding, since a computed value can come
 * back from the server with a slightly different float; dates compare by time.
 */
const areValuesEqual = (a: unknown, b: unknown): boolean => {
  if (isEmptyValue(a) || isEmptyValue(b)) return isEmptyValue(a) && isEmptyValue(b);
  if (typeof a === "number" && typeof b === "number") return Math.abs(a - b) < 1e-6;
  if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime();
  return a === b;
};

/**
 * Defaults can chain - B defaults from A, C from B - and one pass evaluates every default against the
 * data as it was before that pass, so C would read B's old value. Passes repeat until nothing changes;
 * the cap only stops a schema whose defaults reference each other in a cycle.
 */
const MAX_DEFAULT_VALUE_PASSES = 10;

/** One pass of applyComponentDefaultValues, every default read from the same snapshot of the data. */
const applyComponentDefaultValuesOnce = (
  steps: Step[] | undefined,
  data: StandardSpecificData,
  projectDetails: Parameters<typeof createExpressionContext>[1]
): { data: StandardSpecificData; errors: Error[] } => {
  const expressionContext = createExpressionContext(data, projectDetails);
  let result = data;
  const errors: Error[] = [];

  forEachDefaultValueComponent(steps, expressionContext, (component, dataPath) => {
    const storedValue = kanaExpressionInterpreter(`@standardSpecific.${dataPath}`, expressionContext);
    if (!isEmptyValue(storedValue)) return;

    try {
      const defaultValue = evaluateComponentDefaultValue(component, expressionContext);
      if (!isEmptyValue(defaultValue)) {
        result = setStandardSpecificValue(result, dataPath, defaultValue, component.dataType);
      }
    } catch (e) {
      const error = new Error(
        `Failed to evaluate defaultValueExpression "${component.defaultValueExpression}" for "${dataPath}": ${
          e instanceof Error ? e.message : String(e)
        }`
      );
      if (e instanceof Error) error.stack = e.stack;
      errors.push(error);
    }
  });

  return { data: result, errors };
};

/**
 * Writes each visible component's default into the data wherever nothing is stored yet, so the
 * defaults the section displays are also the values it saves. Returns the evaluation failures for
 * the caller to report rather than dropping them.
 */
export const applyComponentDefaultValues = (
  steps: Step[] | undefined,
  data: StandardSpecificData,
  projectDetails: Parameters<typeof createExpressionContext>[1]
): { data: StandardSpecificData; errors: Error[] } => {
  let pass = applyComponentDefaultValuesOnce(steps, data, projectDetails);

  // A pass that writes nothing returns its input, so identity is enough to tell it has settled.
  for (let i = 1; i < MAX_DEFAULT_VALUE_PASSES; i++) {
    const nextPass = applyComponentDefaultValuesOnce(steps, pass.data, projectDetails);
    if (nextPass.data === pass.data) return nextPass;
    pass = nextPass;
  }

  return pass;
};

/** One pass of refreshDependentDefaultValues: the defaults that moved between the two snapshots. */
const refreshDependentDefaultValuesOnce = (
  steps: Step[] | undefined,
  previousData: StandardSpecificData,
  nextData: StandardSpecificData,
  projectDetails: Parameters<typeof createExpressionContext>[1]
): StandardSpecificData => {
  const previousContext = createExpressionContext(previousData, projectDetails);
  const nextContext = createExpressionContext(nextData, projectDetails);
  let result = nextData;

  forEachDefaultValueComponent(steps, nextContext, (component, dataPath) => {
    let previousDefault: unknown;
    let nextDefault: unknown;
    try {
      previousDefault = evaluateComponentDefaultValue(component, previousContext);
      nextDefault = evaluateComponentDefaultValue(component, nextContext);
    } catch {
      return;
    }

    if (areValuesEqual(previousDefault, nextDefault)) return;

    const currentValue = kanaExpressionInterpreter(`@standardSpecific.${dataPath}`, nextContext);
    if (!isEmptyValue(currentValue) && !areValuesEqual(currentValue, previousDefault)) return;

    // "" rather than undefined: every dataType converts "" to empty, while Boolean throws on undefined.
    result = setStandardSpecificValue(result, dataPath, nextDefault ?? "", component.dataType);
  });

  return result;
};

/**
 * Recalculates the fields whose default depends on a value that just changed, e.g. unit capacity
 * after the eligible area is edited. A field only follows its default while it is empty or still
 * holds the default it had before the change - once someone types their own value it is left alone.
 *
 * Every default is re-evaluated against the data before and after the change, so no dependency
 * parsing is needed: a default that did not move is skipped. Each further pass compares the previous
 * pass's input with its output, so a chain (C from B from the edited A) settles in one edit. Failures
 * are skipped here; they are reported when the section saves (see applyComponentDefaultValues).
 */
export const refreshDependentDefaultValues = (
  steps: Step[] | undefined,
  previousData: StandardSpecificData,
  nextData: StandardSpecificData,
  projectDetails: Parameters<typeof createExpressionContext>[1]
): StandardSpecificData => {
  let before = previousData;
  let after = nextData;

  for (let i = 0; i < MAX_DEFAULT_VALUE_PASSES; i++) {
    const refreshed = refreshDependentDefaultValuesOnce(steps, before, after, projectDetails);
    // A pass that writes nothing returns its input, so identity is enough to tell it has settled.
    if (refreshed === after) return after;
    before = after;
    after = refreshed;
  }

  return after;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const convertValueToText = (dataType: DataType, value: any): string => {
  if (value === null || value === undefined) return "";
  switch (dataType) {
    case "Date":
      return convertDateToStringDDMMYYYY(new Date(value));
    case "Boolean":
      if (value === true) return "Yes";
      return "No";
    case "String":
    default:
      return value.toString();
  }
};

export const maybeCreateTooltip = (tooltip?: string): JSX.Element | undefined => {
  if (tooltip) {
    return React.createElement(KanaTooltip, {
      tooltipText: tooltip,
      allowDangerousHtml: true,
    } as TooltipProps);
  }

  return undefined;
};
